// Composition et coût d'un SMS de relance.
//
// Fonctions PURES : le message et son nombre de segments se calculent sans
// réseau, donc se testent. C'est loin d'être cosmétique — un SMS est facturé au
// segment, et un seul caractère hors alphabet GSM triple la facture.

// Alphabet GSM 03.38, jeu de base : tout ce qui tient sur 7 bits.
// Noter que « ç » minuscule N'Y EST PAS (seul « Ç » y figure) : un « reçu » mal
// placé suffit à faire basculer tout le message en UCS-2.
const GSM_BASE =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';

// Jeu étendu : présent en GSM-7, mais chaque caractère compte DOUBLE.
const GSM_ETENDU = '^{}\\[~]|€';

const BASE = new Set(GSM_BASE);
const ETENDU = new Set(GSM_ETENDU);

/** Le texte tient-il dans l'alphabet GSM ? Sinon, l'opérateur passe en UCS-2. */
export function estGsm7(texte: string): boolean {
  // Parcours par point de code (et non par unité UTF-16) : c'est le caractère
  // affiché qui appartient ou non à l'alphabet, pas sa moitié.
  for (const c of texte) {
    if (!BASE.has(c) && !ETENDU.has(c)) return false;
  }
  return true;
}

/**
 * Nombre de SMS réellement facturés.
 *
 * En GSM-7 : 160 caractères, puis des segments de 153 (7 servent au
 * recollement). Les caractères du jeu étendu, « € » en tête, comptent double.
 *
 * Dès qu'un seul caractère sort de l'alphabet GSM, l'opérateur bascule tout le
 * message en UCS-2 : 70 caractères, puis des segments de 67. C'est le piège
 * coûteux — l'espace fine insécable que produit le formatage français des
 * nombres (« 73 583 ») fait passer un message de 1 à 3 SMS sans rien signaler.
 */
export function segmentsSms(texte: string): number {
  if (texte.length === 0) return 0;
  if (!estGsm7(texte)) {
    return texte.length <= 70 ? 1 : Math.ceil(texte.length / 67);
  }
  let unites = 0;
  for (const c of texte) unites += ETENDU.has(c) ? 2 : 1;
  return unites <= 160 ? 1 : Math.ceil(unites / 153);
}

/** Montant en euros, sans centimes, avec une espace ORDINAIRE comme séparateur. */
function montantSms(n: number): string {
  // Volontairement pas Intl.NumberFormat : il sépare les milliers par une
  // espace fine insécable (U+202F), hors alphabet GSM, qui triplerait le coût.
  return Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/**
 * Message de relance pour un devis resté sans réponse.
 *
 * Trois choix délibérés :
 *
 * 1. AUCUN NOM. Le classeur de suivi ne contient que des noms de famille et
 *    des raisons sociales en majuscules — jamais de prénom. « Bonjour
 *    GROSJEAN » est impoli, et « Bonjour COPRO LES LAURIERS » absurde.
 *
 * 2. PAS DE NUMÉRO DE TÉLÉPHONE. Il est redondant : l'opérateur retenu reçoit
 *    les réponses, le client répond donc au SMS. Le retirer fait passer le
 *    message sous 160 caractères, soit UN seul SMS facturé au lieu de deux.
 *
 * 3. SÉPARATEUR DE MILLIERS ORDINAIRE, pour rester dans l'alphabet GSM.
 *
 * Le « STOP » n'est écrit que parce que l'opérateur l'honore vraiment. Avec un
 * opérateur sortant seul, il faudrait retirer cette mention et celle invitant
 * à répondre (voir deploy/SMS-RELANCES-SETUP.md).
 */
export function messageRelanceDevis(devis: { numero: string; montant: number }): string {
  return (
    `Bonjour, IMP TOITURE. Avez-vous pu étudier notre devis ${devis.numero} ` +
    `(${montantSms(devis.montant)} € TTC) ? Nous restons à votre disposition. ` +
    `Répondez à ce SMS. STOP pour vous désinscrire.`
  );
}
